import type { Schemas } from "@norn/sdk"
import { Link, useNavigate, useSearch } from "@tanstack/react-router"
import { ArrowRight, ChevronRight, GitBranch, Search, X } from "lucide-react"
import { useMemo, useState } from "react"
import { type Column, HairlineTable } from "@/components/archetypes/HairlineTable"
import { ErrorState, TableSkeleton } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import { useFormatNumber, useLocale, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { useObjectPages, useOntology } from "@/workspace/hooks"
import { listFields, type Ontology, recordTitle } from "@/workspace/ontology"
import { FieldValue, HiddenChip, RefusalNote } from "@/workspace/values"
import { dataAreas } from "./areas"
import { DataFrame } from "./frame"
import { keyField, type PivotStep, pivotOptions, pivotRows, useTypeScan } from "./pivot"

type Obj = Schemas["ObjectResponse"]
const MAX_STEPS = 3
const SHOWN = 200

export function ExplorePage() {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const search = useSearch({ from: "/$orgId/$environmentId/data/explore" })
  const { ontology, defs } = useOntology(scope)
  const choices = useMemo(
    () => (ontology ? dataAreas(ontology).flatMap((a) => a.types.map((n) => ({ area: a.label, type: ontology.types.get(n) }))).filter((c) => c.type) : []),
    [ontology],
  )
  // Default to a Customer type a source mapping populates (connector-ingested), else any Customer type.
  const mappingText = useMemo(() => (defs.data?.artifacts ?? []).filter((a) => a.kind === "mapping").map((a) => JSON.stringify(a.payload)).join("\n"), [defs.data])
  const customers = choices.filter((c) => c.type?.name.endsWith(".Customer"))
  const mapped = (c: (typeof choices)[number]) => mappingText.includes(JSON.stringify(c.type?.name)) || mappingText.includes(JSON.stringify(c.type?.artifactId))
  const rootType = search.type ?? (customers.find(mapped) ?? customers[0] ?? choices[0])?.type?.name
  return (
    <DataFrame title={t("dv.nav.explore")} description={t("dv.explore.description")} how={["dv.how.explore"]} crumbs={[t("dv.nav.explore")]} width="full">
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology || !rootType ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <>
          <label className="flex max-w-md items-center gap-3 text-sm">
            <span className="shrink-0 text-muted-foreground">{t("dv.explore.start")}</span>
            <select
              value={rootType}
              onChange={(e) => void navigate({ to: "/$orgId/$environmentId/data/explore", params: scope, search: { type: e.target.value } })}
              className="h-9 min-w-0 flex-1 rounded-full border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              {choices.map((c) => (
                <option key={c.type?.name} value={c.type?.name}>
                  {c.area} · {c.type?.pluralLabel}
                </option>
              ))}
            </select>
          </label>
          <Explorer key={rootType} rootType={rootType} ontology={ontology} />
        </>
      )}
    </DataFrame>
  )
}

/** A set of records and up to three pivots from it; each set is a real, authorized read. */
function Explorer({ rootType, ontology }: { rootType: string; ontology: Ontology }) {
  const t = useT()
  const scope = useScope()
  const formatNumber = useFormatNumber()
  const [query, setQuery] = useState("")
  const [steps, setSteps] = useState<PivotStep[]>([])
  const [focus, setFocus] = useState(0)
  const root = useObjectPages(scope, rootType, 200)
  const rootRows = useMemo(() => {
    const rows = root.data?.pages.flatMap((p) => p.items) ?? []
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((o) => o.key.toLowerCase().includes(q) || Object.entries(o.values as Record<string, unknown>).some(([k, v]) => typeof v === "string" && !(o.masked && k in (o.masked as object)) && v.toLowerCase().includes(q)))
  }, [root.data, query])

  // Fixed slots keep hook order stable: each pivot scans its arrival type once.
  const scan1 = useTypeScan(scope, steps[0]?.to)
  const scan2 = useTypeScan(scope, steps[1]?.to)
  const scan3 = useTypeScan(scope, steps[2]?.to)
  const sets = useMemo(() => {
    const scans = [scan1, scan2, scan3]
    const out: { type: string; rows: Obj[] | null; pending: boolean; error: unknown; truncated: boolean; label: string }[] = [
      { type: rootType, rows: rootRows, pending: root.isPending, error: root.error, truncated: Boolean(root.hasNextPage), label: ontology.types.get(rootType)?.pluralLabel ?? rootType },
    ]
    steps.forEach((step, i) => {
      const scan = scans[i]
      const prev = out[i]
      const label = ontology.types.get(step.to)?.pluralLabel ?? step.to
      if (!scan || !prev) return
      // A set inherits its predecessor's state: pending, refused, unsupported or truncated.
      if (prev.pending || prev.error || !prev.rows) {
        out.push({ type: step.to, rows: null, pending: prev.pending, error: prev.error, truncated: prev.truncated, label })
        return
      }
      const rows = scan.data ? pivotRows(ontology, step, prev.rows, scan.data.items) : null
      out.push({ type: step.to, rows, pending: scan.isPending, error: scan.error, truncated: prev.truncated || Boolean(scan.data?.truncated), label })
    })
    return out
  }, [rootType, rootRows, root.isPending, root.error, root.hasNextPage, steps, scan1, scan2, scan3, ontology])

  const focused = sets[Math.min(focus, sets.length - 1)]
  const options = focused?.rows && !focused.pending && focus === sets.length - 1 && steps.length < MAX_STEPS ? pivotOptions(ontology, focused.type) : []
  const pivot = (step: PivotStep) => {
    setSteps((s) => [...s.slice(0, focus), step])
    setFocus(focus + 1)
  }

  return (
    <div className="space-y-6">
      <ol aria-label={t("dv.explore.chain")} className="flex flex-wrap items-center gap-2">
        {sets.map((set, i) => (
          <li key={`${i}:${set.type}`} className="flex items-center gap-2">
            {i > 0 ? <ChevronRight className="size-4 text-subtle-foreground rtl:-scale-x-100" aria-hidden /> : null}
            <button
              type="button"
              aria-current={i === focus ? "step" : undefined}
              onClick={() => setFocus(i)}
              className={cn(
                "inline-flex h-9 items-center gap-2 rounded-full border px-3.5 text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
                i === focus ? "border-foreground/40 bg-accent font-medium" : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {set.label}
              <span className="tabular-nums text-subtle-foreground">
                {set.pending ? "…" : set.rows ? `${formatNumber(set.rows.length)}${set.truncated ? "+" : ""}` : "—"}
              </span>
            </button>
            {i > 0 && i === sets.length - 1 ? (
              <button
                type="button"
                aria-label={t("dv.explore.removeStep")}
                onClick={() => {
                  setSteps((s) => s.slice(0, -1))
                  setFocus((f) => Math.min(f, sets.length - 2))
                }}
                className="flex size-7 items-center justify-center rounded-full text-subtle-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-3">
        {focus === 0 ? (
          <label className="relative block w-full sm:w-72">
            <span className="sr-only">{t("ws.list.filter")}</span>
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" aria-hidden />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setSteps([])
                setFocus(0)
              }}
              placeholder={t("dv.explore.filter")}
              className="h-9 w-full rounded-full border border-input bg-background ps-9 pe-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
            />
          </label>
        ) : null}
        {options.length ? (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t("dv.explore.pivot")}>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <GitBranch className="size-3.5" aria-hidden /> {t("dv.explore.pivot")}
            </span>
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => pivot(o)}
                className="inline-flex h-8 items-center gap-1 rounded-full border border-border px-3 text-xs outline-none transition-colors hover:border-foreground/30 hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/30"
              >
                {o.label}
                {keyField(ontology, o.link) ? (
                  <span dir="ltr" className="font-mono text-2xs text-subtle-foreground">
                    {keyField(ontology, o.link)}
                  </span>
                ) : null}
                <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {focused ? (
        <ResultSet
          set={focused}
          ontology={ontology}
          root={focus === 0}
          more={focus === 0 && root.hasNextPage ? { loading: root.isFetchingNextPage, load: () => void root.fetchNextPage() } : undefined}
        />
      ) : null}
      <p className="text-xs text-subtle-foreground">{t("dv.explore.note")}</p>
    </div>
  )
}

function ResultSet({
  set,
  ontology,
  root,
  more,
}: {
  set: { type: string; rows: Obj[] | null; pending: boolean; error: unknown; truncated: boolean }
  ontology: Ontology
  /** The starting set pages in; pivoted sets show a fixed window of their join. */
  root: boolean
  more?: { loading: boolean; load: () => void }
}) {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const { preference } = useLocale()
  const type = ontology.types.get(set.type)
  if (set.error) return <RefusalNote error={set.error} title={t("dv.explore.refused")} />
  if (set.pending) return <TableSkeleton rows={8} columns={4} />
  if (!set.rows) return <p className="rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">{t("dv.explore.noKey")}</p>
  const loadMore = more ? (
    <button type="button" disabled={more.loading} onClick={more.load} className="h-8 rounded-full border border-border px-3 text-xs hover:bg-muted/60 disabled:opacity-60">
      {more.loading ? t("dv.explore.loading") : t("dv.explore.loadMore")}
    </button>
  ) : null
  if (!set.rows.length)
    return (
      <div className="space-y-2">
        <p className="rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t("dv.explore.empty")}
          {set.truncated ? ` ${t("dv.explore.emptyPartial")}` : ""}
        </p>
        {loadMore}
      </div>
    )
  const fields = type ? listFields(type, 4) : []
  const columns: Column<Obj>[] = [
    {
      key: "title",
      header: type?.label ?? set.type,
      cell: (o) => {
        const title = recordTitle(type, o.values as Record<string, unknown>, o.masked as Record<string, unknown> | undefined, preference.locale)
        return (
          <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: o.objectType, key: o.key }} onClick={(e) => e.stopPropagation()} className="block min-w-0">
            <span dir="auto" className="block truncate font-medium">
              {title.title ?? o.key}
            </span>
            {title.title ? <span className="block truncate text-xs text-muted-foreground">{o.key}</span> : title.hidden ? <HiddenChip className="mt-1 h-5 text-2xs" /> : null}
          </Link>
        )
      },
    },
    ...fields.map(
      (f): Column<Obj> => ({
        key: f.id,
        header: f.label,
        align: f.role === "money" || f.role === "quantity" ? "end" : "start",
        cell: (o) => <FieldValue field={f} values={o.values as Record<string, unknown>} masked={o.masked as Record<string, unknown> | undefined} type={type} />,
      }),
    ),
  ]
  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-2xl border border-border">
        <HairlineTable
          rows={root ? set.rows : set.rows.slice(0, SHOWN)}
          getRowKey={(o) => `${o.objectType}/${o.key}`}
          minWidthClassName="min-w-[640px]"
          onRowClick={(o) => void navigate({ to: "/$orgId/$environmentId/records/$objectType/$key", params: { ...scope, objectType: o.objectType, key: o.key } })}
          columns={columns}
        />
      </div>
      {root ? (
        set.truncated ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-muted-foreground">{t("dv.explore.partialRoot", { shown: set.rows.length })}</p>
            {loadMore}
          </div>
        ) : null
      ) : set.rows.length > SHOWN || set.truncated ? (
        <p className="text-xs text-muted-foreground">{t("dv.explore.partial", { shown: Math.min(SHOWN, set.rows.length) })}</p>
      ) : null}
    </div>
  )
}
